import type { ReactNode } from 'react';

import type { FulfillmentDTO } from '@invenco/sales-interface/interfaces';
import { ShipmentStatus, type ShipmentDTO } from '@invenco/shipping-interface';

import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/core/card';
import { Skeleton } from '@/components/core/skeleton';
import { EMPTY_TEXT_VALUE } from '@/constants';
import { ensureHttp, formatDate } from '@/shared/helpers';
import { fulfilmentServiceTypeTitle } from '@/shared/title-maps';

import { FulfillmentStagePlaceholder, FulfillmentStageTracker } from './fulfillment-stage-tracker';
import { latestShippedAt } from './fulfillment-stages';

type FulfillmentCardProps = {
  fulfillment: FulfillmentDTO;
  shipments: ShipmentDTO[];
  qtyOrdered?: number | null;
  isLoading?: boolean;
  shipmentsError?: unknown;
};

function FulfillmentValue({ value, isLoading }: { value?: ReactNode; isLoading?: boolean }) {
  if (isLoading) return <Skeleton className="h-4 w-28" aria-label="Loading" />;

  return value ?? <span className="text-muted-foreground">{EMPTY_TEXT_VALUE}</span>;
}

function TrackingValue({ shipment }: { shipment: ShipmentDTO }) {
  if (!shipment.trackingNumber) return null;

  const label = `Track: ${shipment.trackingNumber}`;

  if (!shipment.trackingUrl) return <span>{label}</span>;

  return (
    <a
      href={ensureHttp(shipment.trackingUrl)}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline-offset-4 hover:underline"
    >
      {label}
    </a>
  );
}

function FulfillmentFooterRow({ shipment }: { shipment: ShipmentDTO }) {
  if (shipment.status === ShipmentStatus.SHIPPED) {
    const tracking = <TrackingValue shipment={shipment} />;

    return (
      <div className="flex w-full flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <span>
          {shipment.name}
          {shipment.carrierName ? (
            <>
              <span className="text-muted-foreground"> · </span>
              {shipment.carrierName}
            </>
          ) : null}
        </span>
        {tracking ? <span className="text-muted-foreground">{tracking}</span> : null}
      </div>
    );
  }

  return <div className="w-full">{shipment.name}</div>;
}

export function FulfillmentCard({
  fulfillment,
  shipments,
  qtyOrdered,
  isLoading,
  shipmentsError,
}: FulfillmentCardProps) {
  const footerShipments = shipments.filter(
    (shipment) => shipment.status !== ShipmentStatus.CANCELLED,
  );
  const shippedAt = latestShippedAt(footerShipments);
  const details = [
    {
      label: 'Location',
      value: fulfillment.warehouseLocationName ?? fulfillment.locationName,
    },
    {
      label: 'Service',
      value: fulfilmentServiceTypeTitle[fulfillment.fulfillmentServiceType],
    },
    {
      label: 'Date shipped',
      value: shippedAt ? formatDate(shippedAt) : undefined,
    },
    {
      label: 'Items',
      value:
        fulfillment.qtyRequested != null && qtyOrdered != null
          ? `${fulfillment.qtyRequested} of ${qtyOrdered}`
          : undefined,
    },
  ];

  return (
    <Card aria-label={`Fulfillment / ${fulfillment.name}`}>
      <CardHeader>
        <CardTitle>Fulfillment / {fulfillment.name}</CardTitle>
        {!shipmentsError && (
          <CardAction>
            {isLoading ? (
              <FulfillmentStagePlaceholder />
            ) : (
              <FulfillmentStageTracker fulfillment={fulfillment} shipments={shipments} />
            )}
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 @xl/main:grid-cols-4">
          {details.map(({ label, value }) => (
            <div key={label} className="min-w-0 space-y-1">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-medium wrap-break-word">
                <FulfillmentValue value={value} isLoading={isLoading} />
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
      {isLoading ? null : shipmentsError ? (
        <CardFooter>
          <div className="w-full text-muted-foreground">Unable to load shipments</div>
        </CardFooter>
      ) : (
        footerShipments.map((shipment) => (
          <CardFooter key={shipment.id}>
            <FulfillmentFooterRow shipment={shipment} />
          </CardFooter>
        ))
      )}
    </Card>
  );
}
